// ── cohort-recordings-collection: per-session recordings adapter ────────────

import { useMemo } from "react";
import { Badge, useCollection, type CollectionColumnDef, type CollectionFilterDef, type UseCollectionResult } from "@vjal/ui";

import type { CoachingSessionDto } from "../../../services/coaching-dtos";
import type { CoachingRecordingEntry } from "../../../services/coaching-z6-dtos";
import {
  buildCohortSessionFilterDefs,
  cohortSessionFilterInitialState,
  COHORT_TAB_PAGE_SIZE
} from "./cohort-tab-collection-shared";

export type RecordingRow = {
  id: string;
  session_id: string;
  type: string;
  access: string;
  passcode: string;
  fetchedAt: string;
} & Record<string, unknown>;

export function mapRecordingEntries(entries: CoachingRecordingEntry[], sessionId: string): RecordingRow[] {
  return entries.map((entry) => ({
    id: entry.id,
    session_id: sessionId,
    type: entry.recordingType,
    access: entry.accessPolicy,
    passcode: entry.passcodePresent ? "passcode set" : "no passcode",
    fetchedAt: entry.fetchedAt
  }));
}

export function buildRecordingFilterDefs(sessions: CoachingSessionDto[]): CollectionFilterDef<RecordingRow>[] {
  return buildCohortSessionFilterDefs(sessions) as CollectionFilterDef<RecordingRow>[];
}

export function buildRecordingsCollectionColumns(): CollectionColumnDef<RecordingRow>[] {
  return [
    { id: "type", header: "Type", priority: 1, cell: (row) => row.type },
    { id: "access", header: "Access policy", priority: 2, cell: (row) => row.access },
    {
      id: "passcode",
      header: "Passcode",
      priority: 2,
      cell: (row) => <Badge variant={row.passcode === "passcode set" ? "info" : "default"}>{row.passcode}</Badge>
    },
    { id: "fetchedAt", header: "Fetched", priority: 3, cell: (row) => row.fetchedAt }
  ];
}

export function useCohortRecordingsCollection(
  rows: RecordingRow[],
  sessions: CoachingSessionDto[],
  activeSessionId: string
): UseCollectionResult<RecordingRow> {
  const filters = useMemo(() => buildRecordingFilterDefs(sessions), [sessions]);
  const columns = useMemo(() => buildRecordingsCollectionColumns(), []);
  const initialState = useMemo(
    () => cohortSessionFilterInitialState(activeSessionId),
    [activeSessionId]
  );

  return useCollection<RecordingRow>({
    mode: "client",
    rows,
    columns,
    filters,
    pageSize: COHORT_TAB_PAGE_SIZE,
    initialState,
    urlSync: false,
    getRowKey: (row) => row.id
  });
}
